import React, { useId, useState } from "react";

import useTimeState from "hooks/dashboard/useTimeState";
import Plotly, { LayoutAxis } from "plotly.js";
import Plot from "react-plotly.js";
import { I, T } from "services/lang/LanguageService";
import { toInstant } from "utils/time-utils";
import { dweenColorway } from "utils/constants";
import { DatasetDataInfoResponse } from "../../../../api/model/dataset/DatasetDataInfoResponse";
import { APIResponse } from "../../../../services/HttpService";
import ExportCsvDialog from "../ExportCsvDialog";
import ChartWrapper from "../chart-wrapper/ChartWrapper";
import copyChartToClipBoard from "../chartUtils";
import { clipboardIcon, csvDownloadIcon } from "../icons";
import { AxesConfig, DatasetChartProps, PlotDataEx } from "../interfaces";

const commonAxisConfig = {
  showspikes: true,
  spikemode: "toaxis",
  automargin: true,
} as Partial<LayoutAxis>;

const xAxisCommonConfig = {
  spikemode: "toaxis",
  showspikes: true,
};

const layoutConfig = {
  legend: {
    orientation: "h",
    y: -0.2,
  },
  margin: {
    l: 0,
    r: 0,
    b: 30,
    t: 20,
  },
} as Plotly.Layout;

export default function HistogramChart(props: DatasetChartProps) {
  const uniqueId = useId();
  const [exportCsvDialog, setExportCsvDialog] = useState(false);
  const axesConfig = (data: Array<Partial<PlotDataEx>>) => {
    const axes: AxesConfig = {};
    const domain = [0, 1];
    if (data.length > 0) {
      const unit = data[0]?.unit || "";

      axes.xaxis = {
        ...xAxisCommonConfig,
        domain,
        title: {
          text: unit,
          standoff: 10,
        },
      } as LayoutAxis;

      axes.yaxis = {
        title: {
          text: "%",
          standoff: 10,
        },
        ...commonAxisConfig,
      } as LayoutAxis;
    }

    return axes;
  };

  const chartConfig: Partial<Plotly.Config> = {
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: [
      {
        title: T("chart-config.copy-chart-to-clipboard"),
        name: "Copy chart to Clipboard",
        icon: clipboardIcon,
        click: copyChartToClipBoard,
      },
      {
        title: `${T("action.download")} CSV`,
        name: "Download CSV",
        icon: csvDownloadIcon,
        click: () => setExportCsvDialog(true),
      },
    ],
  };

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const promiseHandler = (res: APIResponse<any>) => {
    return res.data.map((ds: DatasetDataInfoResponse) => {
      return mapQueryData(ds);
    });
  };

  const mapQueryData = (ds: DatasetDataInfoResponse) => {
    const x: number[] = [];
    const custom: number[] = [];
    ds.data.forEach(v => {
      x.push(v.value);
    });

    if (props.bins && props.bins.size && props.bins.end && props.bins.start) {
      const sortedNumbers = [...x].sort((a, b) => a - b);
      if (sortedNumbers.length > 0) {
        let currVal = Math.floor(props.bins.start as number);

        while (currVal < Math.ceil(props.bins.end as number)) {
          let count = 0;
          for (let i = 0; i < sortedNumbers.length; i++) {
            if (sortedNumbers[i] >= currVal && sortedNumbers[i] < currVal + (props.bins?.size as number)) {
              count++;
            }
          }
          custom.push(count);
          currVal += props.bins.size as number;
        }
      }
    }

    let datasetTitle: string | undefined = "";
    let barColor: string | undefined = "";

    const currentDataset = props.datasets && props.datasets.find(dataset => dataset.identifier === ds.identifier);
    if (currentDataset) {
      datasetTitle = currentDataset.overriddenTitle || ds.title;
      barColor = currentDataset.color;
    }

    return {
      name: I(datasetTitle),
      type: "histogram",
      unit: ds.unit,
      showlegend: true,
      hovertemplate: `<b>%{y:.2f}%</b> at %{x} <br>${ds.title}<br>${props.bins && props.bins.end && props.bins.start && props.bins.size ? `Count: %{customdata}` : ""}<extra></extra>`,
      x,
      marker: {
        color: barColor,
      },
      histnorm: "percent",
      xbins: props.bins,
      customdata: custom,
    } as unknown as PlotDataEx;
  };

  return (
    <ChartWrapper
      title=""
      uniqueKey={uniqueId}
      identifiers={props.datasets?.map(dataset => dataset.identifier)}
      showDateSelection
      promiseHandler={promiseHandler}
      axesConfig={axesConfig}
      chartConfig={chartConfig}
      updateFrequency={props.updateFrequency}
      height={props.height}
      dateFrom={toInstant(timeState.from)}
      dateTo={toInstant(timeState.to)}
      loadConf={props.loadConf}
      hideRefresh={props.hideRefresh}
      dynamicHeight={props.dynamicHeight}
      referenceTime={props.referenceTime}
      chartDialogOpen={props.chartDialogOpen}
      hideHeader={props.hideHeader}
    >
      {props => (
        <div>
          {exportCsvDialog && (
            <ExportCsvDialog dialog={exportCsvDialog} setDialog={setExportCsvDialog} data={props.data} chartType="HISTOGRAM" />
          )}
          <Plot
            className="ea-plotly-chart"
            config={{ ...props.chartConfig }}
            data={[...props.data]}
            layout={{
              ...props.axesConfig,
              ...layoutConfig,
              width: props.width,
              height: props.height,
              bargap: 0.2,
              colorway: dweenColorway,
            }}
          />
        </div>
      )}
    </ChartWrapper>
  );
}
